<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import type {
  ComponentBlockReferenceTeaserCtaFragment,
  ReferenceEntityResponse,
} from "~~/types/graphql";

const props = defineProps<{
  image: ComponentBlockReferenceTeaserCtaFragment["image"];
  headline: ComponentBlockReferenceTeaserCtaFragment["headline"];
  reference:
    | ComponentBlockReferenceTeaserCtaFragment["reference"]
    | ReferenceEntityResponse;
  page: ComponentBlockReferenceTeaserCtaFragment["page"];
}>();

const parentLink = computed(() => {
  return props.page?.data?.attributes?.Slug
    ? `/${props.page?.data?.attributes?.Slug}/`
    : "";
});

const topText = ref();
const { width, height } = useElementSize(topText);

const { t } = useI18n({
  useScope: "local",
});
</script>
<i18n lang="json">
{
  "en": { "nextProject": "Next Project" },
  "de": { "nextProject": "Zum nächsten Projekt" }
}
</i18n>
<template>
  <section class="reference-teaser-cta container">
    <nuxt-link
      :to="parentLink ? parentLink + reference?.data?.attributes?.slug : ''"
      class="grid sm:grid-cols-12"
    >
      <div
        class="order-2 col-span-12 flex w-fit flex-col p-6 sm:order-1 sm:col-span-7 sm:self-end"
      >
        <span
          v-if="props.headline"
          ref="topText"
          class="top-text w-fit text-h3"
          >{{ headline }}</span
        >
        <span
          class="next-project-text z-10 bg-integer-white font-pixel text-h2-s md:max-2xl:text-h1 2xl:text-h1max"
          >{{ t("nextProject") }}</span
        >
      </div>

      <div
        class="project-teaser order-1 col-span-12 ml-auto w-full overflow-hidden sm:order-2 sm:col-span-5 sm:w-auto"
      >
        <base-image
          v-if="props.image"
          ref="image"
          v-bind="props.image.data?.attributes"
          class="max-h-[320px] min-h-full w-full min-w-full object-cover"
          sizes="(min-width: 640px) 50vw, (max-width: 639px) 100vw"
        />
      </div>
    </nuxt-link>
  </section>
</template>

<style scoped>
.reference-teaser-cta:hover .top-text {
  transform: translateY(0px);
}

.next-project-text {
  min-height: v-bind("height + 'px'");
  min-width: v-bind("width + 'px'");
}

.top-text {
  transition: transform 300ms ease-in-out;
  transform: translateY(v-bind("height + 'px'"));
}

.reference-teaser-cta img {
  transform: scale(1);
  transition: transform 300ms ease;
  overflow: hidden;
}

.reference-teaser-cta:hover img {
  transform: scale(1.1);
  transition: transform 300ms ease;
  overflow: hidden;
}
</style>
